import React, { useContext, useEffect, useState } from "react";
import { IGraphData } from "../../types";
import useScreenSizeDetector from "../useScreenSizeDetector";
import { OrbUserContext } from "../OrbUserContext";
import { OrbConfigUtilsService } from "../../services/OrbConfigUtilsService";

interface GrSingleNumberProps {
    graphData: IGraphData;
    containerWidth?: string;
    containerHeight?: string;
}

interface ChartConfig {
    Main: {
        Title: string;
        SubTitle: string;
        TitlesAlign: string;
        TitlesMarginBottom: number;
        DataColumn: string;
        ColorColumn: string;
        DataFontSize: number;
    };
}

interface IChartData {
    data: any;
    color?: string;
}

export const GrSingleNumber = (props: GrSingleNumberProps): React.ReactElement => {
    const { orbConfig } = useContext(OrbUserContext);
    const { isMobile, isMediumScreen, isLargeScreen } = useScreenSizeDetector();
    const width: string = props.containerWidth ?? "100%";
    const height: string = props.containerHeight ?? "100%";

    const [chartData, setChartData] = useState<IChartData>();
    const [graphConfig, setGraphConfig] = useState<ChartConfig>();

    const rootStyle = {
        width: width,
        height: height,
        justifyContent: "center",
        alignItems: "center",
    };

    useEffect(() => {
        if (!props.graphData) {
            setGraphConfig(undefined);
            setChartData(undefined);
            return;
        }

        const jsonData = props.graphData.jSonProperties;
        const chartConfig: ChartConfig = JSON.parse(jsonData);

        setGraphConfig(chartConfig);

        if (!props.graphData.data || props.graphData.data.length === 0) {
            setChartData(undefined);
            return;
        }

        const value = props.graphData.data[0];
        let color: string | undefined = undefined;
        if (value[chartConfig.Main.ColorColumn]?.trim() !== "") {
            color = value[chartConfig.Main.ColorColumn];
        }

        const data = value[chartConfig.Main.DataColumn];
        setChartData({ data, color });
    }, [props.graphData]);

    const titleTextAlign: React.CSSProperties["textAlign"] =
        ["start", "end", "left", "right", "center", "justify", "match-parent"].includes(
            graphConfig?.Main?.TitlesAlign?.toLocaleLowerCase() || ""
        )
            ? (graphConfig?.Main?.TitlesAlign as React.CSSProperties["textAlign"])
            : "left";

    const titleMarginBottom: string =
        graphConfig?.Main?.TitlesMarginBottom !== undefined
            ? `${graphConfig.Main.TitlesMarginBottom}px`
            : "20px";

    const dataFontSize: string =
        graphConfig?.Main?.DataFontSize !== undefined
            ? `${graphConfig.Main.DataFontSize}px`
            : "150px";

    return (
        <div style={rootStyle}>
            {graphConfig && chartData && (
                <>
                    {graphConfig.Main.Title && graphConfig.Main.Title.length > 0 && (
                        <div style={{ marginBottom: titleMarginBottom }}>
                            <div style={{ textAlign: titleTextAlign }}>
                                {OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.Title, orbConfig)}
                            </div>
                            <div style={{ fontSize: "small", textAlign: titleTextAlign }}>
                                {OrbConfigUtilsService.checkOrbTokens(graphConfig.Main.SubTitle, orbConfig)}
                            </div>
                        </div>
                    )}
                    <div style={{ textAlign: "center" }}>
                        <span
                            style={{
                                fontSize: dataFontSize,
                                fontWeight: "bold",
                                color: chartData.color,
                            }}
                        >
                            {chartData.data}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};
